/**
 * Frontend-side plan catalog — single source of truth for pricing UI.
 *
 * Numeric limits and SLA promises here MUST stay in sync with
 * `backend/plans_config.py` (PLANS dict). When you change a limit there,
 * also update the matching feature copy below.
 *
 * Why duplicate from backend?
 *   The backend dict is the authoritative quota config used by middleware.
 *   The frontend cares about *marketing copy* — phrasing the limit in a way
 *   that sells, plus tier-exclusive promises (priority queue, SLA times)
 *   that don't map to a numeric limit.
 *
 * Prices are €/month. Annual price = monthly * 12 * 0.75 rounded (25% off).
 *
 * `highlights` are the bullets shown on each pricing card. Each bullet is
 * an object so we can later flag "new" or "exclusive" features without
 * breaking the renderer.
 */

export const PLANS = [
    {
        key: "starter",
        name: "Starter",
        monthly: 20,
        annual: 15,
        tagline: "Один бізнес, перший крок",
        highlights: [
            { text: "1 бізнес-профіль" },
            { text: "3 кампанії на бізнес" },
            { text: "3 перегенерації стратегії / міс" },
            { text: "4 аналізи CSV-звітів / міс" },
            { text: "Авто-моніторинг раз на 10 днів" },
            { text: "Зберігання звітів 3 місяці" },
            { text: "Email-сповіщення" },
            { text: "Email-підтримка (до 48 годин)" },
        ],
    },
    {
        key: "growth",
        name: "Growth",
        monthly: 50,
        annual: 37.5,
        tagline: "Активне зростання",
        featured: true,
        // Bullets prefixed with "+" hint at the inheritance from previous tier
        // without making the copy heavy. We render them with a slightly
        // different colour to emphasise additions.
        highlights: [
            { text: "3 бізнес-профілі", upgrade: true },
            { text: "5 кампаній на бізнес", upgrade: true },
            { text: "5 перегенерацій стратегії / міс", upgrade: true },
            { text: "8 аналізів CSV-звітів / міс", upgrade: true },
            { text: "Авто-моніторинг раз на 7 днів", upgrade: true },
            { text: "Зберігання звітів 6 місяців", upgrade: true },
            { text: "Telegram-сповіщення", upgrade: true },
            { text: "Push-сповіщення", upgrade: true },
            { text: "Підтримка до 24 годин", upgrade: true },
        ],
    },
    {
        key: "scale",
        name: "Scale",
        monthly: 100,
        annual: 75,
        tagline: "Кілька напрямків і бізнесів",
        highlights: [
            { text: "5 бізнес-профілів", upgrade: true },
            { text: "10 кампаній на бізнес", upgrade: true },
            { text: "5 перегенерацій стратегії / міс" },
            { text: "12 аналізів CSV-звітів / міс", upgrade: true },
            { text: "Авто-моніторинг раз на 3 дні", upgrade: true },
            { text: "Зберігання звітів 12 місяців", upgrade: true },
            { text: "Пріоритетна черга AI", upgrade: true },
            { text: "Пріоритетна підтримка (до 4 годин)", upgrade: true },
        ],
    },
];

// Currency symbol used everywhere on pricing UI. We standardised on EUR
// because Bizky P1 Sp. z o.o. invoices clients in PL/EU markets.
export const CURRENCY = "€";

// Footer assurance line shown under the cards on both Landing and /pricing.
export const PRICING_ASSURANCE = "Скасування в 1 клік · Дані зберігаються після скасування";
